import { formatNumber } from '@forwardflow/helpers/utils/formatNumber'
import {
   audienceAssignmentBreakdown,
   audienceReasons,
   IAudienceEstimate,
   IAudienceReason,
} from '../utils/audience-estimate'

const detailLink = 'cursor-pointer text-[#C9B8FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9F7BFF]'

const Reasons = ({ rows }: { rows: IAudienceReason[] }) => (
   <ul className="mt-2 space-y-2 text-xs text-white/65">
      {rows.map((reason) => (
         <li key={reason.key} className="flex justify-between gap-4">
            <div>
               {reason.label}
               {reason.note && <p className="mt-0.5 text-white/50">{reason.note}</p>}
            </div>
            <span className="tabular-nums shrink-0">{formatNumber(reason.count)}</span>
         </li>
      ))}
   </ul>
)

export const AudienceEstimate = ({
   estimate, loading, failed, campaignId, onRetry,
}: {
   estimate?: IAudienceEstimate | null
   loading: boolean
   failed: boolean
   campaignId: string | null
   onRetry: () => void
}) => {
   const breakdown = estimate ? audienceAssignmentBreakdown(estimate) : null
   const campaignScope = estimate?.scope === 'campaign'
   const held = estimate?.eligibility?.temporarilyHeldCount || 0
   const holds = audienceReasons(estimate?.eligibility?.temporaryHolds || {}, campaignScope)

   return (
      <section
         className="px-4 py-3 border-b border-white/[0.06]"
         aria-label="Audience estimate"
         data-testid="audience-estimate"
         aria-busy={loading}
      >
         {loading ? (
            <p role="status" className="text-sm text-white/60">Checking estimated leads…</p>
         ) : failed || !estimate ? (
            <div role="status" className="flex flex-wrap items-center gap-2 text-sm text-white/60">
               <span>Lead estimate unavailable.</span>
               <button type="button" onClick={onRetry} className="text-[#C9B8FF] underline underline-offset-2">Retry</button>
            </div>
         ) : (
            <>
               <div className="flex items-baseline gap-2" aria-live="polite">
                  <span className="text-2xl font-semibold tabular-nums text-white" data-testid="estimated-total">{formatNumber(estimate.matchedCount)}</span>
                  <span className="text-sm text-white/70">Estimated leads</span>
               </div>
               <p className="mt-1 text-xs text-white/50">Leads matching your filters.</p>
               <details className="mt-2 text-sm" data-testid="audience-details">
                  <summary className={detailLink}>See details</summary>
                  {breakdown ? (
                     <div className="mt-3 border-t border-white/[0.08]">
                        <div className="py-3 border-b border-white/[0.08]">
                           <div className="flex justify-between gap-4 text-white/85">
                              <span title="Already assigned to this campaign; includes past enrollment after stop checks.">Already assigned</span>
                              <span className="tabular-nums" data-testid="already-assigned-count">{formatNumber(breakdown.alreadyAssigned)}</span>
                           </div>
                        </div>
                        <div className="py-3 border-b border-white/[0.08]">
                           <div className="flex justify-between gap-4 text-white/85">
                              <span>Can be assigned</span>
                              <span className="tabular-nums" data-testid="can-assign-count">{formatNumber(breakdown.canAssign)}</span>
                           </div>
                           {held > 0 && (
                              <details className="mt-1 text-xs" data-testid="hold-details">
                                 <summary className={detailLink}>{formatNumber(held)} of these leads are temporarily on hold</summary>
                                 <Reasons rows={holds} />
                              </details>
                           )}
                        </div>
                        <div className="py-3">
                           <div className="flex justify-between gap-4 text-white/85">
                              <span>Cannot be assigned</span>
                              <span className="tabular-nums" data-testid="cannot-assign-count">{formatNumber(breakdown.cannotAssign)}</span>
                           </div>
                           {breakdown.cannotAssign > 0 && (
                              <details className="mt-1 text-xs" data-testid="exclusion-details">
                                 <summary className={detailLink}>See why</summary>
                                 <Reasons rows={audienceReasons(breakdown.restrictions, campaignScope)} />
                                 <p className="mt-2 text-white/50">Each lead is counted once. Stop rules take priority.</p>
                              </details>
                           )}
                        </div>
                        <p className="mt-2 text-xs text-white/50">
                           {campaignScope && campaignId
                              ? 'Uses current filters and saved campaign settings.'
                              : 'Campaign-specific checks apply after you save the campaign.'}
                           {' '}Final checks still apply before outreach.
                        </p>
                     </div>
                  ) : (
                     <p className="mt-2 text-xs text-white/60">Only filter matches are available. The assignment breakdown is unavailable.</p>
                  )}
               </details>
            </>
         )}
      </section>
   )
}
